import type { Link } from "@circle-react-shared/SettingsNav/NestedNavBar/types";
import type { IconName } from "@circleco/compass/components/Icon";

import { getSettingsGroupSpecs } from "./settingsGroupSpecs";
import type { BuildNavItem, BuildNavSubItem } from "./types";
import type { SettingsNavLinks } from "./useSettingsNavLinks";

const DEFAULT_ITEM_ICON: IconName = "settings-gear";

export const buildSettingsNavItems = (
  links: SettingsNavLinks,
  { includeDisabled = false }: { includeDisabled?: boolean } = {},
): BuildNavItem[] => {
  const allLinks: Link[] = [
    ...links.members,
    ...links.content,
    ...links.settings,
    ...links.paywalls,
    ...links.affiliates,
    ...links.site,
    ...links.email,
    ...links.plans,
    ...links.plus,
    ...links.developers,
  ];
  const linkByPath = new Map(allLinks.map(link => [link.path, link]));

  const resolveLink = (spec: {
    alternatePaths?: string[];
    path: string;
  }): Link | undefined => {
    let disabledLink: Link | undefined;
    for (const path of [spec.path, ...(spec.alternatePaths ?? [])]) {
      const link = linkByPath.get(path);
      if (link && !link.isDisabled) {
        return link;
      }
      disabledLink ??= link;
    }
    return includeDisabled ? disabledLink : undefined;
  };

  return getSettingsGroupSpecs()
    .map(group => {
      const children = group.items.reduce<BuildNavSubItem[]>((acc, spec) => {
        const link = resolveLink(spec);
        if (link) {
          acc.push({
            id: link.path,
            label: spec.labelOverride ?? link.label,
            description: spec.description,
            icon: spec.icon ?? DEFAULT_ITEM_ICON,
            releasedAt: spec.releasedAt,
            to: link.path,
            exact: link.exact,
          });
        }
        return acc;
      }, []);
      return { group, children };
    })
    .filter(({ children }) => children.length > 0)
    .map(({ group, children }) => ({
      id: group.id,
      label: group.label,
      icon: group.icon,
      compassIcon: group.compassIcon,
      path: children[0].to,
      children,
    }));
};
